import React, { useRef } from 'react';
import { Editor } from '@tiptap/react';
import { 
  Bold, 
  Italic, 
  List, 
  ListOrdered,
  Image as ImageIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Upload
} from 'lucide-react';
import { fontFamilies } from '../../constants/editor';

interface ToolbarProps {
  editor: Editor;
  onImageClick: (e: React.MouseEvent, files: FileList) => void;
  editorId: string;
}

export const Toolbar: React.FC<ToolbarProps> = ({ editor, onImageClick, editorId }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onImageClick(e.nativeEvent as unknown as React.MouseEvent, e.target.files);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800 p-2 border-b space-y-2">
      <div className="flex space-x-2 pb-2 border-b border-gray-200 dark:border-gray-700">
        <select
          onChange={(e) => {
            editor.chain().focus().setFontFamily(e.target.value).run();
          }}
          value={editor.getAttributes('textStyle').fontFamily}
          className="px-2 py-1 rounded border dark:bg-gray-700 dark:border-gray-600"
        >
          <option value="">Fonte</option>
          {fontFamilies.map((font) => (
            <option key={font.value} value={font.value}>{font.name}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('bold') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Negrito"
        >
          <Bold className="h-5 w-5" />
        </button>
        
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('italic') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Itálico"
        >
          <Italic className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('bulletList') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Lista com marcadores"
        >
          <List className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive('orderedList') ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Lista numerada"
        >
          <ListOrdered className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Alinhar à esquerda"
        >
          <AlignLeft className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Centralizar"
        >
          <AlignCenter className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${
            editor.isActive({ textAlign: 'right' }) ? 'bg-gray-200 dark:bg-gray-700' : ''
          }`}
          title="Alinhar à direita"
        >
          <AlignRight className="h-5 w-5" />
        </button>

        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            id={`image-upload-${editorId}`}
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          <label
            htmlFor={`image-upload-${editorId}`}
            className="flex items-center space-x-1 p-2 rounded cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
            title="Inserir imagem (máximo 5)"
          >
            <ImageIcon className="h-5 w-5" />
            <Upload className="h-4 w-4" />
          </label>
        </div>
      </div>
    </div>
  );
};